import { useState } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@ai-tutor/ui';

interface LearningPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function LearningPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  isLoading = false,
}: LearningPaginationProps) {
  const [jumpToPage, setJumpToPage] = useState('');

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleJumpToPage = () => {
    const page = parseInt(jumpToPage);
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
      setJumpToPage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };

  // Generate page numbers to show
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Always include first page
    range.push(1);

    // Add pages around current page
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Always include last page if > 1
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

    // Add dots where there are gaps
    let prev = 0;
    for (const page of uniqueRange) {
      if (page - prev > 1) {
        rangeWithDots.push('...');
      }
      rangeWithDots.push(page);
      prev = page;
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={`border-t border-border bg-transparent`}>
      <div className='mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8'>
        <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
          {/* Results Info */}
          <div className={`text-sm text-muted-foreground`}>
            Showing <span className='font-medium'>{startItem}</span> to{' '}
            <span className='font-medium'>{endItem}</span> of{' '}
            <span className='font-medium'>{totalItems}</span> results
          </div>

          {/* Pagination Controls */}
          <div className='flex items-center gap-2'>
            {/* Previous Button */}
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              // Below `sm` the label is hidden and the chevron alone is
              // drawn, which left an unnamed button; the name stays put.
              aria-label='Previous page'
              className='tap-target h-9 px-3'
            >
              <ChevronLeft className='mr-1 h-4 w-4' />
              <span className='hidden sm:inline'>Previous</span>
            </Button>

            {/* Page Numbers */}
            <div className='hidden items-center gap-1 md:flex'>
              {visiblePages.map((page, index) => {
                if (page === '...') {
                  return (
                    <div key={`dots-${index}`} className='px-3 py-2'>
                      <MoreHorizontal
                        className={`h-4 w-4 text-muted-foreground`}
                      />
                    </div>
                  );
                }

                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size='sm'
                    onClick={() => onPageChange(page as number)}
                    disabled={isLoading}
                    className='h-9 w-9 p-0'
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            {/* Mobile Page Info */}
            <div
              className={`flex items-center px-3 py-2 text-sm text-muted-foreground md:hidden`}
            >
              Page {currentPage} of {totalPages}
            </div>

            {/* Next Button */}
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              aria-label='Next page'
              className='tap-target h-9 px-3'
            >
              <span className='hidden sm:inline'>Next</span>
              <ChevronRight className='ml-1 h-4 w-4' />
            </Button>
          </div>

          {/* Jump to Page (Desktop only) */}
          <div className='hidden items-center gap-2 lg:flex'>
            <span className={`text-sm text-muted-foreground`}>Go to:</span>
            <input
              type='number'
              min='1'
              max={totalPages}
              value={jumpToPage}
              onChange={e => setJumpToPage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder='Page'
              className={`w-16 rounded-sm border border-border bg-card px-2 py-1 text-sm text-foreground`}
              disabled={isLoading}
            />
            <Button
              variant='outline'
              size='sm'
              onClick={handleJumpToPage}
              disabled={!jumpToPage || isLoading}
              className='h-8 px-3 text-xs'
            >
              Go
            </Button>
          </div>
        </div>

        {/* Progress Indicator (when loading) */}
        {isLoading && (
          <div className='mt-2'>
            <div className={`h-1 w-full rounded-full bg-muted`}>
              <div
                className='h-1 animate-pulse rounded-full bg-blue-600 dark:bg-primary'
                style={{ width: '60%' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
